// Chat file upload helper for web
// Uses the chat service endpoints (not media service) to match mobile implementation

const API_BASE_URL = 'https://api.familypal.com/familypal/v1';

export interface ChatAttachment {
  id: string;
  type: string;
  uri: string;
  name: string;
  size: number;
  mimeType: string;
  thumbnail?: string;
  thumbnailKey?: string;
  width?: number;
  height?: number;
  miniPreview?: string;
}

function normalizeAttachment(a: any): ChatAttachment {
  return {
    id: a.id || '',
    type: a.type || '',
    uri: a.uri || '',
    name: a.name || '',
    size: typeof a.size === 'string' ? parseInt(a.size, 10) : (a.size || 0),
    mimeType: a.mime_type || a.mimeType || '',
    thumbnail: a.thumbnail || '',
    thumbnailKey: a.thumbnail_key || a.thumbnailKey || '',
    width: a.width || 0,
    height: a.height || 0,
    miniPreview: a.mini_preview || a.miniPreview || '',
  };
}

/**
 * Upload a file to the chat service using presigned URL flow:
 * 1. Get presigned PUT URL from chat service
 * 2. Upload file directly to storage
 * 3. Register the upload to create the attachment record
 */
export async function uploadChatFile(
  file: { uri: string; name: string; mimeType: string; size: number },
  onProgress?: (progress: number) => void,
): Promise<ChatAttachment> {
  // Determine attachment type for the backend proto enum
  let attachmentType = 'ATTACHMENT_TYPE_FILE';
  if (file.mimeType?.startsWith('image/')) {
    attachmentType = 'ATTACHMENT_TYPE_IMAGE';
  } else if (file.mimeType?.startsWith('video/')) {
    attachmentType = 'ATTACHMENT_TYPE_VIDEO';
  } else if (file.mimeType?.startsWith('audio/')) {
    attachmentType = 'ATTACHMENT_TYPE_AUDIO';
  }

  // Step 1: Get presigned upload URL
  const uploadUrlResponse = await fetch(`${API_BASE_URL}/chat/files/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      filename: file.name,
      type: attachmentType,
      mime_type: file.mimeType,
      file_size: file.size,
    }),
  });

  if (!uploadUrlResponse.ok) {
    const errorText = await uploadUrlResponse.text();
    throw new Error(`Failed to get upload URL: ${uploadUrlResponse.status} ${errorText}`);
  }

  const uploadInfo = await uploadUrlResponse.json();
  const presignedUrl = uploadInfo.upload_url || uploadInfo.uploadUrl;
  const attachmentId = uploadInfo.attachment_id || uploadInfo.attachmentId;

  if (!presignedUrl || !attachmentId) {
    throw new Error('Invalid upload URL response');
  }

  onProgress?.(10);

  // Step 2: Upload file directly to presigned URL
  const blob = await fetch(file.uri).then((r) => r.blob());
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.mimeType || 'application/octet-stream',
    },
    body: blob,
  });

  if (!uploadResponse.ok) {
    throw new Error(`File upload failed: ${uploadResponse.status}`);
  }

  onProgress?.(80);

  // Step 3: Register the upload to create the attachment record
  const registerResponse = await fetch(`${API_BASE_URL}/chat/files/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      attachment_id: attachmentId,
      filename: file.name,
      type: attachmentType,
      mime_type: file.mimeType,
      file_size: file.size,
    }),
  });

  if (!registerResponse.ok) {
    const errorText = await registerResponse.text();
    throw new Error(`Failed to register upload: ${registerResponse.status} ${errorText}`);
  }

  const registered = await registerResponse.json();

  onProgress?.(100);

  return normalizeAttachment({ ...registered, id: attachmentId });
}

/**
 * Get a download URL for a chat attachment
 */
export async function getChatFileDownloadUrl(fileId: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/chat/files/${fileId}/download-url`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get download URL: ${response.status}`);
  }

  const data = await response.json();
  return data.download_url || data.downloadUrl || data.url;
}
